import { useRef } from "react";
import { generatePath, Link } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { CrmMasterSummary } from "@/lib/api/crm/masters";
import { RoutePath } from "../../routerPaths";
import { recordTypeOrder } from "../recordTypes";
import { RecordTypeBadge } from "./RecordTypeBadge";

type CrmMasterCardListProps = {
  /** Masters to show, in display order, each with only the records to show. */
  masters: CrmMasterSummary[];
  emptyMessage: string;
};

/** Virtualized list with one card per master, its records grouped by record type. */
export function CrmMasterCardList({ masters, emptyMessage }: CrmMasterCardListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: masters.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 140,
    overscan: 4,
    getItemKey: (index) => masters[index].id,
  });

  if (masters.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-6 text-center text-sm text-muted-foreground shadow-lg">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="max-h-[calc(100vh-310px)] overflow-y-scroll"
      data-testid="crm-list-cards"
    >
      <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((item) => (
          <div
            key={item.key}
            ref={virtualizer.measureElement}
            data-index={item.index}
            className="absolute left-0 top-0 w-full pb-3"
            style={{ transform: `translateY(${item.start}px)` }}
          >
            <CrmMasterCard master={masters[item.index]} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CrmMasterCard({ master }: { master: CrmMasterSummary }) {
  const groups = recordTypeOrder.flatMap((type) => {
    const records = master.records
      .filter((record) => record.record_type === type)
      .sort((a, b) => a.long_name.localeCompare(b.long_name, undefined, { sensitivity: "base" }));
    return records.length > 0 ? [{ type, records }] : [];
  });

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm" data-testid={`crm-master-card-${master.id}`}>
      <Link
        to={generatePath(RoutePath.CrmGroup, { masterId: master.id })}
        className="text-base font-semibold text-blue-700 hover:underline"
        data-testid={`crm-master-link-${master.id}`}
      >
        {master.long_name}
      </Link>
      {groups.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No records</p>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          {groups.map(({ type, records }) => (
            <div key={type} className="flex flex-col gap-1.5">
              <RecordTypeBadge type={type} className="self-start" />
              <ul className="flex flex-col gap-1 pl-2">
                {records.map((record) => (
                  <li key={record.id}>
                    <Link
                      to={generatePath(RoutePath.CrmRecordDetails, { recordId: record.id })}
                      className="text-sm text-gray-800 hover:text-blue-700 hover:underline"
                      data-testid={`crm-record-link-${record.id}`}
                    >
                      {record.long_name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
